import { useState } from "react";
import { Apple, CheckCircle2, Download as DownloadIcon, AlertTriangle, Shield, ChevronDown, ChevronUp } from "lucide-react";

const DOWNLOAD_URL = "https://github.com/ZepioCs/Nexatice-Releases/releases/latest/download/Nexatice-1.0.3-setup.exe";

const platforms = [
  {
    name: "Windows",
    icon: (
      <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
        <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
      </svg>
    ),
    description: "Windows 10 or later",
    features: ["Native performance", "Auto-updates", "Shell integration"],
    available: true,
    downloadUrl: DOWNLOAD_URL,
  },
  {
    name: "macOS",
    icon: <Apple className="w-10 h-10" />,
    description: "Coming later",
    features: ["Apple Silicon support", "Dark mode", "Notarized"],
    available: false,
  },
  {
    name: "Linux",
    icon: (
      <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.132 1.884 1.071.771-.06 1.592-.536 2.257-1.306.631-.765 1.683-1.084 2.378-1.503.348-.199.629-.469.649-.853.023-.4-.2-.811-.714-1.376v-.097l-.003-.003c-.17-.2-.25-.535-.338-.926-.085-.401-.182-.786-.492-1.046h-.003c-.059-.054-.123-.067-.188-.135a.357.357 0 00-.19-.064c.431-1.278.264-2.55-.173-3.694-.533-1.41-1.465-2.638-2.175-3.483-.796-1.005-1.576-1.957-1.56-3.368.026-2.152.236-6.133-3.544-6.139z" />
      </svg>
    ),
    description: "Coming later",
    features: ["AppImage format", "No installation", "Portable"],
    available: false,
  },
];

export function Download() {
  const [showInstructions, setShowInstructions] = useState(false);

  const handleDownload = () => {
    window.location.href = DOWNLOAD_URL;
  };

  return (
    <section id="download" className="relative py-32 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Download Nexatice
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto text-lg">
            Get started with the free version. No account required.
          </p>
        </div>

        <div className="max-w-2xl mx-auto mb-8">
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-amber-400 font-semibold mb-1">Unsigned Software Notice</h4>
                <p className="text-amber-200/80 text-sm">
                  This software is not code-signed yet. Windows SmartScreen may show a warning when you run the installer.
                  This is normal for indie software without an expensive signing certificate.
                </p>
                <button
                  onClick={() => setShowInstructions(!showInstructions)}
                  className="flex items-center gap-1 text-amber-400 hover:text-amber-300 text-sm mt-2 font-medium"
                >
                  {showInstructions ? "Hide" : "How to install anyway"}
                  {showInstructions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showInstructions && (
                  <div className="mt-3 p-3 bg-black/30 rounded-lg text-sm text-zinc-300 space-y-2">
                    <p><strong className="text-white">1.</strong> Run the downloaded installer</p>
                    <p><strong className="text-white">2.</strong> When SmartScreen appears, click <span className="text-emerald-400">"More info"</span></p>
                    <p><strong className="text-white">3.</strong> Click <span className="text-emerald-400">"Run anyway"</span></p>
                    <p className="text-zinc-500 text-xs mt-2">
                      The app is safe - you can scan the installer on{" "}
                      <a href="https://www.virustotal.com" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 underline">
                        VirusTotal
                      </a>{" "}
                      if you want to verify.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {platforms.map((platform, index) => (
            <div
              key={platform.name}
              className="platform-card group opacity-0 animate-fade-in-up relative overflow-hidden"
              style={{ 
                animationDelay: `${index * 100}ms`,
                animationFillMode: 'forwards'
              }}
            >
              {!platform.available && (
                <div className="absolute inset-0 bg-zinc-900/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
                  <span className="px-3 py-1 rounded-full text-xs font-medium border bg-zinc-800 text-zinc-500 border-zinc-700">
                    Coming Later
                  </span>
                </div>
              )}
              
              <div className={`mb-6 flex justify-center ${platform.available ? "text-emerald-400" : "text-zinc-600"}`}>
                {platform.icon}
              </div>
              
              <h3 className={`text-2xl font-bold mb-2 text-center ${platform.available ? "text-white" : "text-zinc-500"}`}>
                {platform.name}
              </h3>
              
              <p className={`text-sm mb-6 text-center ${platform.available ? "text-zinc-400" : "text-zinc-600"}`}>
                {platform.description}
              </p>
              
              <ul className="space-y-2 mb-6">
                {platform.features.map((feature) => (
                  <li key={feature} className={`flex items-center gap-2 text-sm ${platform.available ? "text-zinc-400" : "text-zinc-600"}`}>
                    <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${platform.available ? "text-emerald-500" : "text-zinc-700"}`} />
                    {feature}
                  </li>
                ))}
              </ul>

              {platform.available && (
                <button
                  onClick={handleDownload}
                  className="w-full py-3 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 hover:shadow-lg hover:shadow-emerald-500/25 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <DownloadIcon className="w-5 h-5" />
                  Download for {platform.name}
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="text-center">
          <div className="inline-flex flex-wrap items-center justify-center gap-4 px-6 py-4 rounded-2xl bg-white/5 border border-white/10">
            <span className="text-sm text-zinc-400">Includes:</span>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {["Multi-database support", "Query Editor", "Data Browser", "Auto-updates"].map((feature) => (
                <span key={feature} className="flex items-center gap-1.5 text-sm text-zinc-300">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  {feature}
                </span>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 mt-6 text-zinc-500 text-sm">
            <Shield className="w-4 h-4" />
            <span>Open source on GitHub • No account required • Free forever</span>
          </div>
        </div>
      </div>
    </section>
  );
}
